import jwtDecode from 'jwt-decode';
import { deserializeSession, deserializeUser, serializeSession } from './auth.io';
import { Session, User } from './types';

const languages = require('../data/languages');

export const isBrowser = typeof window !== `undefined`;

const CLOUD_API = process.env.GATSBY_CLOUD_API_URL;
const CLIENT_ID = process.env.GATSBY_OAUTH_CLIENT_ID;

const CURRENT_USER_URL = `${CLOUD_API}user_account/api/v3/users/current/`;
const AUTHORIZE_URL = `${CLOUD_API}oauth2/authorize`;

const WELCOME_PAGE_PATH = {
  en: '/welcome-to-pix4d',
  de: '/de/willkommen-bei-pix4d',
  es: '/es/bienvenido-a-pix4d',
  fr: '/fr/bienvenue-a-pix4d',
  pt: '/pt/bem-vindo-a-pix4d',
  it: '/it/benvenuto-a-pix4d',
};

export const SESSION_STORAGE_KEY = 'pix4d-session';
export const USER_STORAGE_KEY = 'pix4d-user';

const getValidSession = (session: Session): Session | undefined => {
  const jwt = jwtDecode(session.accessToken);

  const expiry = new Date(0);
  expiry.setUTCSeconds(jwt.exp);
  return expiry.valueOf() > new Date().valueOf() ? session : undefined;
};

const addLanguageToLoginUrlIfAny = (url: string, lang: string): string => {
  return `${url}&lang=${lang}`;
};

const getCurrentUrlLanguage = (pathname: string): string | undefined => {
  if (pathname === '/') {
    return;
  }

  const allLanguagesCode = Object.keys(languages.enabledLanguages).filter(languageCode => languageCode !== 'en-US');
  const regexpLanguageCode = allLanguagesCode.reduce((accumulator, languageCode) => accumulator+= languageCode+"|", '');

  const pathMatched = pathname.match(new RegExp(`/((${regexpLanguageCode})+)/`));
  let matchedString = pathMatched ? pathMatched[1] : '';

  const language = languages.enabledLanguages[matchedString];
  /**
   * Solve compatibility issue with cloud-next
   * code for jp is ja on that repository
   */
  if (language === languages.enabledLanguages.jp) {
    matchedString = 'ja';
  }

  return language ? matchedString : undefined;
};

const getWelcomePageUrl = (currentLang: string): string => {
  const landPageExists = Object.keys(WELCOME_PAGE_PATH).includes(currentLang);
  return landPageExists ? WELCOME_PAGE_PATH[currentLang] : WELCOME_PAGE_PATH['en'];
}

export const getLoginUrl = (enableWelcomePage: boolean = false): string => {
  const { origin, href, pathname } = window.location;
  if (!origin || !href) {
    return '#';
  }

  const currentLang = getCurrentUrlLanguage(pathname);

  const redirectHref = enableWelcomePage ? `${origin}${getWelcomePageUrl(currentLang || 'en')}` : href;

  const token = Math.random().toString(24).substring(2);
  const url = `${AUTHORIZE_URL}?redirect_uri=${encodeURIComponent(
    `${origin}/oauth-callback`,
  )}&response_type=token&state=${encodeURIComponent(
    `${token}-${redirectHref}`,
  )}&client_id=${CLIENT_ID}`;

  return currentLang ? addLanguageToLoginUrlIfAny(url, currentLang) : url;
};

export const getLogoutUrl = (isGated: boolean = false): string => {
  const { origin, href } = window.location;

  if (!origin || !href) {
    return '#';
  }

  const redirectHref = isGated ? origin : href;
  const url = `${CLOUD_API}logout?redirect_uri=${redirectHref}&client_id=${CLIENT_ID}`;
  return url;
};

export class AuthService {
  session: Session | undefined;
  user: any | undefined;

  get isAuthenticated(): boolean {
    return Boolean(this.session);
  }

  async init() {
    this.session = this.get();
    this.user = this.getUser();
  }

  get(): Session | undefined {
    if (!isBrowser) {
      return undefined;
    }
    try {
      const data: string | null = window.localStorage.getItem(SESSION_STORAGE_KEY);
      if (!data) {
        return undefined;
      }
      const session = deserializeSession(JSON.parse(data));
      return getValidSession(session);
    } catch (e) {
      console.log('unable to get session from storage', e);
      return undefined;
    }
  }

  sessionFromFragment = (fragment: string): Record<string, string> => {
    const params = new URLSearchParams(fragment);
    const result: Record<string, string> = {};
    const keys = ['access_token', 'token_type', 'state'];

    keys.forEach(key => {
      const value = params.get(key);
      if (value !== null) {
        result[key] = value;
      }
    });
    return result;
  };

  pathFromState = (state): string => {
    if (!state) {
      return '';
    }
    const matches = state.match(/(^[^-]+)-(.*$)/);
    if (!matches) {
      return '';
    }

    const path = new URL(matches[2]);

    if (path.origin !== window.location.origin) {
      return '';
    }

    return path.href;
  };

  parse(location: Location) {
    const fragment = location.hash.substr(1);
    const params = this.sessionFromFragment(fragment);
    const path = this.pathFromState(params.state);

    this.set({
      accessToken: params.access_token,
      tokenType: params.token_type,
    });

    this.fetchUser()
      .then(user => {
        if (user) {
          this.setUser(user);
          window.location.replace(path);
        }
      })
      .catch(err => {
        this.user = null;
        console.error(err);
      });
  }

  private set(session: Session): void {
    try {
      this.session = session;
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(serializeSession(session)));
    } catch (e) {
      console.log('unable to persist session in storage', e);
    }
  }

  getUser(): User | undefined {
    if (!isBrowser) {
      return undefined;
    }
    try {
      const data: string | null = window.localStorage.getItem(USER_STORAGE_KEY);
      if (!data) {
        return undefined;
      }
      return JSON.parse(data);
    } catch (e) {
      console.log('unable to get session from storage', e);
      return undefined;
    }
  }

  private setUser(user: User): void {
    try {
      this.user = user;
      window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } catch (e) {
      console.log('unable to persist session in storage', e);
    }
  }

  private fetchUser = (): Promise<User | null> => {
    if (!this.session) {
      return Promise.resolve(null);
    }
    const token = this.session.accessToken;
    return fetch(`${CURRENT_USER_URL}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(userJson => deserializeUser(userJson));
  };

  clear() {
    try {
      if(typeof window !== "undefined") {
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
        window.localStorage.removeItem(USER_STORAGE_KEY);
      }
    } catch (e) {
      console.log('unable to remove session from storage', e);
    }
  }
}
